import { useState } from "react";
import { toast } from "sonner";
import { CalendarIcon, ArrowUpRight } from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import Reveal from "@/components/site/Reveal";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BRAND, CATEGORIES } from "@/lib/content";

const TIME_SLOTS = [
    "10:30 AM",
    "11:30 AM",
    "12:30 PM",
    "02:30 PM",
    "03:30 PM",
    "04:30 PM",
    "05:30 PM",
    "06:30 PM",
    "07:30 PM",
];

export default function Appointment() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        enquiryType: "retail",
        interest: "",
        date: null,
        time: "",
        notes: "",
    });

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please share your name and phone number.");
            return;
        }
        if (!form.date || !form.time) {
            toast.error("Please pick a date and time for your visit.");
            return;
        }

        const enquiryLabel =
            form.enquiryType === "wholesale"
                ? "Wholesale / Bulk Order"
                : "Retail / Personal";

        const msg = [
            `*New In-Store Appointment · Hoofs and Hides*`,
            ``,
            `*Enquiry Type:* ${enquiryLabel}`,
            `*Name:* ${form.name}`,
            `*Phone:* ${form.phone}`,
            form.email ? `*Email:* ${form.email}` : null,
            `*Interested in:* ${form.interest || "Not specified"}`,
            `*Preferred date:* ${format(form.date, "EEEE, d MMMM yyyy")}`,
            `*Preferred time:* ${form.time}`,
            form.notes ? `*Notes:* ${form.notes}` : null,
            ``,
            `Please confirm my visit. Thank you.`,
        ]
            .filter(Boolean)
            .join("\n");

        const url = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(msg)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success("Opening WhatsApp — send the message to confirm.");
    };

    return (
        <section
            id="appointment"
            data-testid="appointment-section"
            className="bg-[#0a0a0a] text-white px-6 sm:px-10 lg:px-16 py-28 lg:py-40 border-t border-white/10 scroll-mt-20"
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                <div className="lg:col-span-5 lg:sticky lg:top-32">
                    <Reveal>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-8">
                            06 · Your visit
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.95] tracking-tight">
                            Book a
                            <br />
                            <em className="font-normal">private</em>
                            <br />
                            fitting.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-10 max-w-sm text-base text-white/60 font-light leading-relaxed">
                            Tell us when you'd like to visit. We'll prepare swatches,
                            lasts and measurements ahead of your arrival — and greet
                            you by name.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <div className="mt-10 space-y-4 text-sm text-white/70 font-light">
                            <p>{BRAND.address.line1}</p>
                            <p>{BRAND.address.line2}</p>
                            <p>{BRAND.address.line3}</p>
                            <p className="text-white/50 text-xs uppercase tracking-[0.24em] pt-2">
                                {BRAND.hours.weekdays}
                            </p>
                        </div>
                    </Reveal>
                </div>

                <div className="lg:col-span-7">
                    <form
                        onSubmit={onSubmit}
                        data-testid="appointment-form"
                        noValidate
                        className="space-y-10"
                    >
                        <Reveal delay={0.02}>
                            <div>
                                <span className="block text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
                                    Enquiry Type
                                </span>
                                <div
                                    role="tablist"
                                    data-testid="enquiry-type-tabs"
                                    className="inline-flex border border-white/15"
                                >
                                    {[
                                        {
                                            slug: "retail",
                                            label: "Retail / Personal",
                                        },
                                        {
                                            slug: "wholesale",
                                            label: "Wholesale / Bulk",
                                        },
                                    ].map((opt) => {
                                        const active = form.enquiryType === opt.slug;
                                        return (
                                            <button
                                                key={opt.slug}
                                                type="button"
                                                role="tab"
                                                aria-selected={active}
                                                data-testid={`enquiry-${opt.slug}`}
                                                onClick={() =>
                                                    update("enquiryType", opt.slug)
                                                }
                                                className={`px-6 py-3 text-[11px] uppercase tracking-[0.26em] transition-all duration-400 ${
                                                    active
                                                        ? "bg-white text-black"
                                                        : "text-white/65 hover:text-white"
                                                }`}
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {form.enquiryType === "wholesale" && (
                                    <p
                                        data-testid="wholesale-hint"
                                        className="mt-4 text-xs text-white/55 font-light max-w-md"
                                    >
                                        For bulk and corporate orders, please share
                                        approximate quantity and timeline in the notes
                                        below — our atelier will respond with lead
                                        times and pricing.
                                    </p>
                                )}
                            </div>
                        </Reveal>

                        <Reveal delay={0.05}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <Field label="Full Name">
                                    <input
                                        data-testid="input-name"
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => update("name", e.target.value)}
                                        placeholder="Your name"
                                        className="input-line"
                                    />
                                </Field>
                                <Field label="Phone">
                                    <input
                                        data-testid="input-phone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={(e) =>
                                            update("phone", e.target.value)
                                        }
                                        placeholder="+91 · · · · ·"
                                        className="input-line"
                                    />
                                </Field>
                            </div>
                        </Reveal>

                        <Reveal delay={0.12}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <Field label="Email (optional)">
                                    <input
                                        data-testid="input-email"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) =>
                                            update("email", e.target.value)
                                        }
                                        placeholder="you@example.com"
                                        className="input-line"
                                    />
                                </Field>
                                <Field label="Interested In">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger
                                            data-testid="input-interest"
                                            asChild
                                        >
                                            <button
                                                type="button"
                                                className="input-line text-left flex items-center justify-between cursor-pointer"
                                            >
                                                <span
                                                    className={
                                                        form.interest
                                                            ? "text-white"
                                                            : "text-zinc-500"
                                                    }
                                                >
                                                    {form.interest ||
                                                        "Select a category"}
                                                </span>
                                                <span className="text-white/40 text-xs">
                                                    ▾
                                                </span>
                                            </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent
                                            align="start"
                                            className="bg-[#0f0f0f] border border-white/10 text-white min-w-[16rem] rounded-sm"
                                        >
                                            {CATEGORIES.map((c) => (
                                                <DropdownMenuItem
                                                    key={c.slug}
                                                    data-testid={`interest-opt-${c.slug}`}
                                                    onClick={() =>
                                                        update(
                                                            "interest",
                                                            c.title +
                                                                (c.custom
                                                                    ? " (Custom-Made)"
                                                                    : ""),
                                                        )
                                                    }
                                                    className="text-white/80 focus:bg-white/10 focus:text-white cursor-pointer text-sm py-2"
                                                >
                                                    {c.title}
                                                    {c.custom && (
                                                        <span className="ml-2 text-[10px] uppercase tracking-widest text-white/40">
                                                            · Custom
                                                        </span>
                                                    )}
                                                </DropdownMenuItem>
                                            ))}
                                            <DropdownMenuItem
                                                onClick={() =>
                                                    update("interest", "General visit")
                                                }
                                                className="text-white/80 focus:bg-white/10 focus:text-white cursor-pointer text-sm py-2"
                                                data-testid="interest-opt-general"
                                            >
                                                Just browsing
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </Field>
                            </div>
                        </Reveal>

                        <Reveal delay={0.18}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <Field label="Preferred Date">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <button
                                                type="button"
                                                data-testid="input-date"
                                                className="input-line text-left flex items-center justify-between cursor-pointer"
                                            >
                                                <span
                                                    className={
                                                        form.date
                                                            ? "text-white"
                                                            : "text-zinc-500"
                                                    }
                                                >
                                                    {form.date
                                                        ? format(
                                                              form.date,
                                                              "EEE, d MMM yyyy",
                                                          )
                                                        : "Pick a date"}
                                                </span>
                                                <CalendarIcon
                                                    size={14}
                                                    className="text-white/40"
                                                />
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            align="start"
                                            className="w-auto p-0 bg-[#0f0f0f] border border-white/10 rounded-sm"
                                        >
                                            <Calendar
                                                mode="single"
                                                selected={form.date}
                                                onSelect={(d) => update("date", d)}
                                                disabled={(d) =>
                                                    d <
                                                    new Date(
                                                        new Date().setHours(
                                                            0,
                                                            0,
                                                            0,
                                                            0,
                                                        ),
                                                    )
                                                }
                                                initialFocus
                                                className="bg-[#0f0f0f] text-white"
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </Field>
                                <Field label="Preferred Time">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger
                                            data-testid="input-time"
                                            asChild
                                        >
                                            <button
                                                type="button"
                                                className="input-line text-left flex items-center justify-between cursor-pointer"
                                            >
                                                <span
                                                    className={
                                                        form.time
                                                            ? "text-white"
                                                            : "text-zinc-500"
                                                    }
                                                >
                                                    {form.time || "Select a time"}
                                                </span>
                                                <span className="text-white/40 text-xs">
                                                    ▾
                                                </span>
                                            </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent
                                            align="start"
                                            className="bg-[#0f0f0f] border border-white/10 text-white rounded-sm max-h-72 overflow-auto"
                                        >
                                            {TIME_SLOTS.map((s) => (
                                                <DropdownMenuItem
                                                    key={s}
                                                    data-testid={`time-opt-${s.replace(/\s|:/g, "")}`}
                                                    onClick={() => update("time", s)}
                                                    className="text-white/80 focus:bg-white/10 focus:text-white cursor-pointer text-sm py-2 font-mono-custom"
                                                >
                                                    {s}
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </Field>
                            </div>
                        </Reveal>

                        <Reveal delay={0.24}>
                            <Field label="Notes for the Atelier (optional)">
                                <textarea
                                    data-testid="input-notes"
                                    value={form.notes}
                                    onChange={(e) => update("notes", e.target.value)}
                                    placeholder="Measurements you'd like taken, a piece you'd like recreated, anything we should prepare…"
                                    rows={3}
                                    className="input-line resize-none"
                                />
                            </Field>
                        </Reveal>

                        <Reveal delay={0.3}>
                            <div className="pt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                                <p className="text-xs text-white/40 max-w-sm font-light leading-relaxed">
                                    On submit, your details will open a pre-filled
                                    WhatsApp message to the store. No data is stored.
                                </p>
                                <motion.button
                                    whileHover={{ x: 4 }}
                                    transition={{ duration: 0.4 }}
                                    type="submit"
                                    data-testid="submit-appointment"
                                    className="group inline-flex items-center gap-4 border border-white px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-colors duration-500"
                                >
                                    Send on WhatsApp
                                    <ArrowUpRight
                                        size={14}
                                        className="transition-transform duration-500 group-hover:rotate-45"
                                    />
                                </motion.button>
                            </div>
                        </Reveal>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
                {label}
            </span>
            {children}
        </label>
    );
}
